// src/pages/client/SearchResultsPage.jsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Pagination,
  Container
} from '@mui/material';

import { fetchPackages } from '../../features/packages/packagesSlice';
import { setClientFilter } from '../../features/filters/filtersSlice';
import PackageCard from '../../components/client/PackageCard';
import PackageFilters from '../../components/client/PackageFilters';

const SearchResultsPage = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { packages, isLoading, error, meta } = useSelector(state => state.packages);
  const { clientFilters } = useSelector(state => state.filters);

  // Récupérer les paramètres de recherche depuis l'URL
  useEffect(() => {
    const params = {};
    
    // Récupérer tous les paramètres de recherche
    for (const [key, value] of searchParams.entries()) {
      if (value) {
        params[key] = value;
        
        // Mettre à jour les filtres dans le state Redux
        dispatch(setClientFilter({ name: key, value }));
      }
    }
    
    // Si une recherche est définie, l'utiliser
    if (searchParams.get('q')) {
      params.search = searchParams.get('q');
      dispatch(setClientFilter({ name: 'search', value: params.search }));
    }
    
    // Si un numéro de page est défini, l'utiliser
    const page = parseInt(searchParams.get('page'), 10) || 1;
    dispatch(setClientFilter({ name: 'page', value: page }));
    
    // Récupérer les forfaits avec les filtres
    dispatch(fetchPackages({ ...clientFilters, ...params }));
  }, [dispatch, searchParams]);

  // Gestionnaire de changement de page
  const handlePageChange = (event, value) => {
    dispatch(setClientFilter({ name: 'page', value }));
    
    // Mettre à jour les paramètres de recherche
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', value);
    
    // Remplacer l'URL actuelle sans recharger la page
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${newSearchParams.toString()}`
    );
    
    // Récupérer les forfaits pour la nouvelle page
    dispatch(fetchPackages({ ...clientFilters, page: value }));
  };

  // Gestionnaire de changement de filtre
  const handleFilterChange = (name, value) => {
    dispatch(setClientFilter({ name, value }));
    
    // Mettre à jour les paramètres de recherche
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (value === '' || value === null) {
      newSearchParams.delete(name);
    } else {
      newSearchParams.set(name, value);
    }
    
    // Réinitialiser la page à 1 lors d'un changement de filtre
    if (name !== 'page') {
      newSearchParams.set('page', 1);
    }
    
    // Remplacer l'URL actuelle sans recharger la page
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${newSearchParams.toString()}`
    );
    
    // Récupérer les forfaits avec le nouveau filtre
    dispatch(fetchPackages({ 
      ...clientFilters, 
      [name]: value,
      page: name !== 'page' ? 1 : clientFilters.page 
    }));
  };

  // Extraire le terme de recherche pour l'afficher
  const searchTerm = searchParams.get('q') || '';

  return (
    <Box sx={{ py: 4 }}>
      <Container>
        <Typography variant="h4" component="h1" gutterBottom>
          {searchTerm 
            ? `Résultats de recherche pour "${searchTerm}"`
            : 'Recherche de forfaits Hajj'}
        </Typography>
        
        <Grid container spacing={3}>
          {/* Sidebar avec filtres */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, mb: { xs: 2, md: 0 } }}>
              <Typography variant="h6" gutterBottom>
                Filtres
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <PackageFilters 
                filters={clientFilters} 
                onFilterChange={handleFilterChange} 
              />
            </Paper>
          </Grid>
          
          {/* Résultats de recherche */}
          <Grid item xs={12} md={9}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : packages.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Aucun forfait ne correspond à votre recherche
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Essayez de modifier vos critères de recherche.
                </Typography>
              </Paper>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {meta.total} forfait{meta.total > 1 ? 's' : ''} trouvé{meta.total > 1 ? 's' : ''}
                </Typography>
                
                <Grid container spacing={3}>
                  {packages.map((pkg) => (
                    <Grid item xs={12} sm={6} lg={4} key={pkg._id}>
                      <PackageCard package={pkg} />
                    </Grid>
                  ))}
                </Grid>
                
                {/* Pagination */}
                {meta.pages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination 
                      count={meta.pages} 
                      page={meta.page} 
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default SearchResultsPage;